import React, {useEffect, useState} from 'react'
import api from "../../api";
import {Col, Row, Card, Input, Button, Table, Tag, message, Form, Tabs, Select, Popconfirm, Divider, Alert} from "antd";
import {CopyOutlined, DeleteOutlined, LockOutlined, GlobalOutlined, BankOutlined} from '@ant-design/icons';
import './style.css'
import PlanBox from "../../Components/PlanBox";
import HelpVideoLoader from "../../Components/HelpVideoLoader";
import {useTranslations} from "../../lang";
import dayjs from 'dayjs';
import {formatPlanPrice, isValidIban} from "../../helpers";
import {useSearchParams} from "react-router-dom";

export function AffiliateAmount({amount, currency}) {
    return <span>{formatPlanPrice(amount, currency)}</span>
}

export function AffiliateTotals({totals}) {
    return <span>{Object.entries(totals || {})
        .sort(([currencyA], [currencyB]) => currencyA.localeCompare(currencyB))
        .map(([currency, amount]) => formatPlanPrice(amount, currency))
        .join(' / ')}</span>
}

export default function Account() {

    const [plan, setPlan] = useState(false)
    const [company, setCompany] = useState(false)
    const [plans, setPlans] = useState(false)
    const [affiliateInfo, setAffiliateInfo] = useState(null)
    const {translate: t} = useTranslations()
    const [passwordForm] = Form.useForm()
    const [regionForm] = Form.useForm()
    const [ibanForm] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [searchParams] = useSearchParams()
    const paymentFailed = searchParams.get('payment_failed')

    useEffect(() => {
        api.get_plan().then(setPlan)
        api.getMyCompany().then((companyData) => {
            setCompany(companyData)
            api.getAllPlans(companyData.billing_currency || companyData.country).then(setPlans)
            regionForm.setFieldsValue({
                country: companyData.country,
                language: companyData.language
            })
            ibanForm.setFieldsValue({
                affiliate_iban: companyData.affiliate_iban || ''
            })
        })
        api.getAffiliateInfo().then(setAffiliateInfo).catch(err => {
            console.error('Error loading affiliate info:', err)
        })
    }, [regionForm, ibanForm])

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text)
        message.success(t('Link gekopieerd naar klembord!'))
    }

    const handleChangePassword = async (values) => {
        if (values.newPassword !== values.confirmPassword) {
            message.error(t('Wachtwoorden komen niet overeen'))
            return
        }
        setLoading(true)
        try {
            await api.changePassword(values.currentPassword, values.newPassword)
            message.success(t('Wachtwoord succesvol gewijzigd'))
            passwordForm.resetFields()
        } catch (error) {
            message.error(t('Wachtwoord wijzigen mislukt. Controleer je huidige wachtwoord.'))
        }
        setLoading(false)
    }

    const handleRegionChange = async (values) => {
        setLoading(true)
        try {
            await api.updateCompany({
                ...company,
                country: values.country,
                language: values.language
            })
            message.success(t('Regio instellingen opgeslagen'))
            setCompany({...company, ...values})
        } catch (error) {
            message.error(t('Opslaan mislukt'))
        }
        setLoading(false)
    }

    const handleDeleteAccount = async () => {
        setLoading(true)
        try {
            await api.deleteAccount()
            message.success(t('Account verwijderd'))
            localStorage.removeItem('token')
            window.location.href = '/'
        } catch (error) {
            message.error(t('Account verwijderen mislukt'))
        }
        setLoading(false)
    }

    const handleIbanChange = async (values) => {
        setLoading(true)
        try {
            await api.updateCompany({
                ...company,
                affiliate_iban: values.affiliate_iban
            })
            message.success(t('IBAN opgeslagen'))
            setCompany({...company, affiliate_iban: values.affiliate_iban})
        } catch (error) {
            message.error(t('Opslaan mislukt'))
        }
        setLoading(false)
    }

    const tabItems = [
        {
            key: 'plan',
            label: t('Mijn pakket'),
            children: (
                <>
                    {plans && plan && (
                        <Row gutter={[90, 24]}>
                            {plans
                                .filter(p => {
                                    if (p.is_active) return true;
                                    if (!p.is_active && p.id === plan.id) return true;
                                    return false;
                                })
                                .map(p => (
                                    <Col md={8} xs={24} key={`plan${p.id}`}>
                                        <PlanBox plan={p} isActive={p.id === plan.id} company={company}></PlanBox>
                                    </Col>
                                ))}
                        </Row>
                    )}
                    <div className="mar-t-2">
                        <HelpVideoLoader />
                    </div>
                </>
            )
        },
        {
            key: 'affiliate',
            label: t('Tip een vriend'),
            children: (
                <>
                    {affiliateInfo && (
                        <Card>
                            <h2>{t('Tip een vriend')}</h2>
                            <p>
                                {t('Deel Oogst.app met je vrienden en verdien 33% commissie op alle succesvolle betalingen! Wanneer iemand zich via jouw link aanmeldt en een betaling doet, ontvang je commissie.')}
                            </p>

                            <Row>
                                <Col span={18}>
                                    <Form.Item label={t('Jouw unieke affiliate link:')}>
                                        <Input
                                            value={affiliateInfo.affiliate_link}
                                            readOnly
                                        />
                                    </Form.Item>
                                </Col>
                                <Col>
                                    <Button
                                        className="mar-l-1"
                                        type="primary"
                                        icon={<CopyOutlined />}
                                        onClick={() => copyToClipboard(affiliateInfo.affiliate_link)}
                                    >
                                        {t('Kopieer')}
                                    </Button>
                                </Col>
                            </Row>

                            <Row gutter={16} className="mar-b-2">
                                <Col xs={24} sm={8}>
                                    <Card className="text-center affiliate-stat-card">
                                        <h4 className="affiliate-stat-value">
                                            <AffiliateTotals totals={affiliateInfo.total_earned} />
                                        </h4>
                                        <p className="affiliate-stat-label">{t('Verdiend')}</p>
                                    </Card>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Card className="text-center affiliate-stat-card">
                                        <h4 className="affiliate-stat-value">
                                            <AffiliateTotals totals={affiliateInfo.total_pending} />
                                        </h4>
                                        <p className="affiliate-stat-label">{t('In afwachting')}</p>
                                    </Card>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Card className="text-center affiliate-stat-card">
                                        <h4 className="affiliate-stat-value">
                                            {affiliateInfo.referred_companies.length}
                                        </h4>
                                        <p className="affiliate-stat-label">{t('Aanmeldingen')}</p>
                                    </Card>
                                </Col>
                            </Row>

                            {affiliateInfo.referred_companies.length > 0 && (
                                <div>
                                    <h4>{t('Jouw verwijzingen')}</h4>
                                    <Table
                                        dataSource={affiliateInfo.referred_companies}
                                        rowKey="id"
                                        pagination={false}
                                        columns={[
                                            {
                                                title: t('Naam'),
                                                dataIndex: 'name',
                                                key: 'name',
                                            },
                                            {
                                                title: t('Aangemeld op'),
                                                dataIndex: 'created_at',
                                                key: 'created_at',
                                                render: (date) => dayjs(date).format('DD-MM-YYYY')
                                            }
                                        ]}
                                    />
                                </div>
                            )}

                            {affiliateInfo.payments.length > 0 && (
                                <div className="mar-t-2">
                                    <h4>{t('Commissie overzicht')}</h4>
                                    <Table
                                        dataSource={affiliateInfo.payments}
                                        rowKey="id"
                                        pagination={{ pageSize: 5 }}
                                        columns={[
                                            {
                                                title: t('Bedrag'),
                                                dataIndex: 'payout_amount',
                                                key: 'payout_amount',
                                                render: (amount, payment) => (
                                                    <AffiliateAmount amount={amount} currency={payment.currency} />
                                                )
                                            },
                                            {
                                                title: t('Uitbetaling datum'),
                                                dataIndex: 'payout_date',
                                                key: 'payout_date',
                                                render: (date) => dayjs(date).format('DD-MM-YYYY')
                                            },
                                            {
                                                title: t('Status'),
                                                dataIndex: 'is_paid_out',
                                                key: 'is_paid_out',
                                                render: (isPaid) => (
                                                    <Tag color={isPaid ? 'green' : 'orange'}>
                                                        {isPaid ? t('Uitbetaald') : t('In afwachting')}
                                                    </Tag>
                                                )
                                            }
                                        ]}
                                    />
                                </div>
                            )}

                            <Divider />

                            <div>
                                <h4><BankOutlined className="mar-r-1" />{t('Uitbetaling')}</h4>
                                <p>{t('Vul je IBAN nummer in waarop je de commissie wilt ontvangen.')}</p>
                                <Form
                                    form={ibanForm}
                                    layout="vertical"
                                    onFinish={handleIbanChange}
                                >
                                    <Row>
                                        <Col xs={24} sm={16}>
                                            <Form.Item
                                                name="affiliate_iban"
                                                label={t('IBAN')}
                                                rules={[
                                                    {
                                                        validator: (_, value) => {
                                                            if (!value || isValidIban(value)) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(new Error(t('Vul een geldig IBAN nummer in')));
                                                        },
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="NL00 ABCD 0123 4567 89" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={8} className="mar-l-1">
                                            <Form.Item label=" ">
                                                <Button type="primary" htmlType="submit" loading={loading}>
                                                    {t('Opslaan')}
                                                </Button>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Form>
                            </div>
                        </Card>
                    )}
                </>
            )
        },
        {
            key: 'settings',
            label: t('Mijn instellingen'),
            children: (
                <Row gutter={24}>
                    <Col xs={24} md={12}>
                        <Card title={<><GlobalOutlined className="mar-r-1" />{t('Regio instellingen')}</>}>
                            <Form
                                form={regionForm}
                                layout="vertical"
                                onFinish={handleRegionChange}
                            >
                                <Form.Item
                                    name="country"
                                    label={t('Land')}
                                >
                                    <Select>
                                        <Select.Option value="nl">Nederland</Select.Option>
                                        <Select.Option value="be">België</Select.Option>
                                        <Select.Option value="de">Duitsland</Select.Option>
                                        <Select.Option value="fr">Frankrijk</Select.Option>
                                        <Select.Option value="uk">Verenigd Koninkrijk</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item
                                    name="language"
                                    label={t('Taal')}
                                >
                                    <Select>
                                        <Select.Option value="nl">Nederlands</Select.Option>
                                        <Select.Option value="de">Duits</Select.Option>
                                        <Select.Option value="fr">Frans</Select.Option>
                                        <Select.Option value="en">Engels</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Button type="primary" htmlType="submit" loading={loading}>
                                    {t('Opslaan')}
                                </Button>
                            </Form>
                        </Card>
                    </Col>

                    <Col xs={24} md={12}>
                        <Card title={<><LockOutlined className="mar-r-1" />{t('Wachtwoord wijzigen')}</>}>
                            <Form
                                form={passwordForm}
                                layout="vertical"
                                onFinish={handleChangePassword}
                            >
                                <Form.Item
                                    name="currentPassword"
                                    label={t('Huidig wachtwoord')}
                                    rules={[{ required: true, message: t('Vul je huidige wachtwoord in') }]}
                                >
                                    <Input.Password />
                                </Form.Item>
                                <Form.Item
                                    name="newPassword"
                                    label={t('Nieuw wachtwoord')}
                                    rules={[{ required: true, message: t('Vul een nieuw wachtwoord in') }]}
                                >
                                    <Input.Password />
                                </Form.Item>
                                <Form.Item
                                    name="confirmPassword"
                                    label={t('Bevestig nieuw wachtwoord')}
                                    rules={[{ required: true, message: t('Bevestig je nieuwe wachtwoord') }]}
                                >
                                    <Input.Password />
                                </Form.Item>
                                <Button type="primary" htmlType="submit" loading={loading}>
                                    {t('Wachtwoord wijzigen')}
                                </Button>
                            </Form>
                        </Card>
                    </Col>

                    <Col xs={24} className="mar-t-2">
                        <Card title={<><DeleteOutlined className="mar-r-1" />{t('Account verwijderen')}</>}>
                            <p>{t('Let op: dit kan niet ongedaan worden gemaakt. Al je gegevens worden permanent verwijderd.')}</p>
                            <Popconfirm
                                title={t('Account verwijderen')}
                                description={t('Weet je zeker dat je je account wilt verwijderen? Dit kan niet ongedaan worden gemaakt.')}
                                onConfirm={handleDeleteAccount}
                                okText={t('Ja, verwijder mijn account')}
                                cancelText={t('Annuleren')}
                                okButtonProps={{ danger: true }}
                            >
                                <Button danger loading={loading}>
                                    {t('Account verwijderen')}
                                </Button>
                            </Popconfirm>
                        </Card>
                    </Col>
                </Row>
            )
        }
    ]

    return (
        <div>
            {paymentFailed && (
                <Alert
                    message={t('De betaling is mislukt')}
                    description={t('Je betaling is helaas niet gelukt. Kies hieronder een pakket om het opnieuw te proberen.')}
                    type="error"
                    showIcon
                    closable
                    className="mar-b-2"
                />
            )}
            <h1>{t('Mijn account')}</h1>
            <p>{t('Welkom bij je account. Hier kun je je accountgegevens inzien en je abonnement aanpassen.')}</p>

            <Tabs items={tabItems} defaultActiveKey="plan" />
        </div>
    )
}
